import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Btn from '../../../../../components/Buttons/Btn';
import Modal from '../../../../../components/Modal';
import { backendErrorNotification, frontendNotification } from '../../../../../components/Notification';
import SelectAsyncJSX from '../../../../../components/SelectAsync';
import { audienceAPI, orderApprovalGroupsApi } from '../../../../../services/requests';
import { Audience } from '../../../../../types';
import { OrderApprovalGroup } from '../../../../../types/apiResponse/orderApprovalGroup';
import { getOptionsFromAudiences, SelectOption } from '../../../../../utils/getSelectOptions';
import checkPermission from '../../../../../utils/checkPermission';
import CreateAudienceModal from '../../../Registrations/Audience/Components/ModalCreate/CreateAudienceModal';
import * as s from '../styles';

export type ApprovalGroupFormProps = {
  selectedGroup: OrderApprovalGroup | null;
  closeModal: () => void;
  callback: () => void;
};

const ApprovalGroupFormModal = ({ selectedGroup, closeModal, callback }: ApprovalGroupFormProps) => {
  const { t } = useTranslation();
  const [description, setDescription] = useState(selectedGroup?.description ?? '');
  const [authority, setAuthority] = useState(String(selectedGroup?.authority ?? ''));
  const [audience, setAudience] = useState<SelectOption | null>(selectedGroup?.audience ? {
    value: selectedGroup.audience.id,
    label: selectedGroup.audience.description,
  } : null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAudienceModal, setShowAudienceModal] = useState(false);
  const [audienceCache, setAudienceCache] = useState(0);

  const save = async () => {
    const authorityValue = Number(authority);
    if (!description.trim() || !audience || !Number.isInteger(authorityValue)
      || authorityValue < 1 || authorityValue > 100) {
      frontendNotification({ message: t('approvalGroupRequiredFields'), type: 'warning' });
      return;
    }

    setIsLoading(true);
    try {
      const payload = { description: description.trim(), audienceId: Number(audience.value), authority: authorityValue };
      orderApprovalGroupsApi.query = '';
      if (selectedGroup) {
        await orderApprovalGroupsApi.update(payload, selectedGroup.id);
      } else {
        await orderApprovalGroupsApi.store(payload);
      }
      frontendNotification({ message: t('success'), type: 'success' });
      callback();
      closeModal();
    } catch (err) {
      backendErrorNotification(err as AxiosError<any, any>);
    } finally {
      setIsLoading(false);
    }
  };

  const onAudienceCreated = (createdAudience?: Audience) => {
    if (createdAudience?.id !== undefined) {
      setAudience({ value: createdAudience.id, label: createdAudience.description });
    }
    setAudienceCache((value) => value + 1);
    setShowAudienceModal(false);
  };

  return (
    <>
      <Modal
        showModal
        title={t(selectedGroup ? 'editApprovalGroup' : 'newApprovalGroup') as string}
        closeModal={closeModal}
        cancelButton={closeModal}
        action={save}
        isLoading={isLoading}
        disableActionBtn={showAudienceModal}
        bodyStyle={{ paddingLeft: '1rem', paddingRight: '1rem', boxSizing: 'border-box' }}
      >
        <s.FormContent>
          <div>
            <s.Label htmlFor="approval-group-description">{t('description')}</s.Label>
            <input
              id="approval-group-description"
              className="input"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              maxLength={255}
              disabled={isLoading}
              required
            />
          </div>
          <div>
            <s.Label htmlFor="approval-group-authority">{t('approvalAuthority')}</s.Label>
            <input
              id="approval-group-authority"
              className="input"
              type="number"
              min={1}
              max={100}
              step={1}
              value={authority}
              onChange={(event) => setAuthority(event.target.value)}
              disabled={isLoading}
              required
            />
            <s.HelpText>{t('approvalAuthorityHelp')}</s.HelpText>
          </div>
          <s.AudienceRow>
            <s.AudienceField>
              <s.Label htmlFor="approval-group-audience">{t('audience')}</s.Label>
              <SelectAsyncJSX
                id="approval-group-audience"
                value={audience}
                onChange={setAudience}
                request={audienceAPI}
                reqResponseToOption={getOptionsFromAudiences}
                placeholder={t('selectAudience') as string}
                disabled={isLoading}
                isRequired
                cacheUniqs={[audienceCache]}
              />
            </s.AudienceField>
            {checkPermission('audience-create') && (
              <Btn
                text={t('newAudience') as string}
                type="outlined"
                onClick={() => setShowAudienceModal(true)}
                disabled={isLoading}
                style={{ margin: 0 }}
              />
            )}
          </s.AudienceRow>
        </s.FormContent>
      </Modal>
      {showAudienceModal && (
        <CreateAudienceModal closeModal={() => setShowAudienceModal(false)} callback={onAudienceCreated}
          availableTypes={['user', 'allUsers']} allowAutomated />
      )}
    </>
  );
};

export default ApprovalGroupFormModal;
